import { Box, Stack, Typography } from "@mui/material";
import { EditableRowsTable, FormikTextField } from "@shared";

import { SystemDropdownCategory } from "@/api";
import { FormikAutocomplete } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocomplete";
import { FormikAutocompleteSelect } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocompleteSelect";
import { FormikDateTimePicker } from "@/common/components/fields/formik/formik-datetime-picker/FormikDateTimePicker";
import { useDropdownOptions } from "@/common/hooks/useDropdownOptions";
import { useSystemDropdownOptions } from "@/common/hooks/useSystemDropdownOptions";
import {
  SrvCourseEntryFormValues,
  SrvProceedingFormik,
} from "@/pages/clients/client/components/tabs/debt-settlement-tab/tabs/srv-proceeding-tab/SrvProceedingTab.types";
import { useTranslation } from "@/translations";

// A single-line input strips the line breaks out of its own value, and the "Tagsatzung" template the edict
// transfer writes is a form that only reads as one.
const RemarkMaxRows = 4;

interface Props {
  formik: SrvProceedingFormik;
}

function entryPath<K extends keyof SrvCourseEntryFormValues>(index: number, field: K): `courseEntries.${number}.${K}` {
  return `courseEntries.${index}.${field}`;
}

const createEmptyEntry = (): SrvCourseEntryFormValues => ({
  id: undefined,
  date: null,
  entryType: null,
  result: null,
  remark: "",
});

export function CourseTable({ formik }: Props) {
  const { t } = useTranslation();
  const { srvCourseEntryResults } = useDropdownOptions();
  const { optionItems: srvCourseEntryTypes } = useSystemDropdownOptions(SystemDropdownCategory.SrvCourseEntryType);
  const entries = formik.values.courseEntries;

  const addRow = () => {
    void formik.setFieldValue("courseEntries", [...entries, createEmptyEntry()]);
  };

  const removeRow = (index: number) => {
    void formik.setFieldValue(
      "courseEntries",
      entries.filter((_, i) => i !== index)
    );
  };

  return (
    <Stack gap={3}>
      <Typography variant="h5">{t("clients.client.debtSettlement.srvProceeding.sections.course")}</Typography>
      <EditableRowsTable
        columns={[
          { label: t("clients.client.debtSettlement.srvProceeding.course.date"), size: 3 },
          { label: t("clients.client.debtSettlement.srvProceeding.course.type"), size: 3 },
          { label: t("clients.client.debtSettlement.srvProceeding.course.result"), size: 3 },
          { label: t("clients.client.debtSettlement.srvProceeding.course.remark"), size: 2 },
          { size: 1 },
        ]}
        rows={entries.map((entry, index) => ({
          key: entry.id ?? `new-${index}`,
          cells: (
            <>
              <Box sx={{ flex: "0 0 25%" }}>
                <FormikDateTimePicker formik={formik} path={entryPath(index, "date")} required />
              </Box>
              <Box sx={{ flex: "0 0 25%" }}>
                <FormikAutocompleteSelect
                  formik={formik}
                  path={entryPath(index, "entryType")}
                  options={srvCourseEntryTypes}
                />
              </Box>
              <Box sx={{ flex: "0 0 25%" }}>
                <FormikAutocomplete formik={formik} path={entryPath(index, "result")} options={srvCourseEntryResults} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <FormikTextField formik={formik} path={entryPath(index, "remark")} multiline maxRows={RemarkMaxRows} />
              </Box>
            </>
          ),
        }))}
        addRowLabel={t("clients.client.debtSettlement.srvProceeding.course.addRow")}
        onAddRow={addRow}
        onRemoveRow={removeRow}
      />
    </Stack>
  );
}
